/**
 * Simple Cached Image Component - Option 2 Implementation
 * 
 * Provides memory caching for instant page navigation.
 * Works alongside enhanced service worker for cross-session persistence.
 * 
 * Features:
 * - Memory cache for same-session instant loading
 * - Graceful fallback to normal image loading
 * - No URL modification - uses original image URLs
 * - Backward compatible with existing image components
 */

import React, { useState, useEffect } from 'react';

// Simple memory cache for the browser session
const imageCache = new Map<string, {
  imageElement: HTMLImageElement;
  timestamp: number;
  url: string;
}>();

// Cache cleanup - remove old entries (max 100 images)
const MAX_CACHE_SIZE = 100;
const cleanupCache = () => {
  if (imageCache.size > MAX_CACHE_SIZE) {
    // Remove oldest 20 entries
    const entries = Array.from(imageCache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    for (let i = 0; i < 20; i++) {
      imageCache.delete(entries[i][0]);
    }
  }
};

interface CachedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  fallbackSrc?: string;
  preserveAspectRatio?: boolean;
  aspectRatio?: string; // e.g., "1/1" for square, "16/9" for widescreen
}

export const CachedImage: React.FC<CachedImageProps> = ({
  src,
  alt,
  className,
  loading = 'lazy',
  onLoad,
  onError,
  fallbackSrc = '/food.jpg',
  preserveAspectRatio = false,
  aspectRatio = '1/1'
}) => {
  const [currentSrc, setCurrentSrc] = useState<string>(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Reset states when src changes
    setHasError(false);
    setIsLoading(true);
    
    // Check memory cache first
    const cached = imageCache.get(src);
    if (cached) {
      // Instant load from memory cache
      setCurrentSrc(src);
      setIsLoading(false);
      onLoad?.();
      return;
    }

    // Not in memory cache, load normally (service worker will handle caching)
    const img = new Image();
    
    const handleImageLoad = () => {
      // Store in memory cache for next time
      imageCache.set(src, {
        imageElement: img,
        timestamp: Date.now(),
        url: src
      });
      
      // Cleanup cache if too large
      cleanupCache();
      
      setCurrentSrc(src);
      setIsLoading(false);
      onLoad?.();
    };

    const handleImageError = () => {
      console.warn('⚠️ [CachedImage] Failed to load:', src.split('/').pop());
      setHasError(true);
      setCurrentSrc(fallbackSrc);
      setIsLoading(false);
      onError?.();
    };

    img.onload = handleImageLoad;
    img.onerror = handleImageError;
    
    // Start loading
    img.src = src;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, onLoad, onError, fallbackSrc]);

  // If preserveAspectRatio is enabled, wrap in aspect ratio container
  if (preserveAspectRatio) {
    return (
      <div 
        className={`${className} relative overflow-hidden`}
        style={{ aspectRatio }}
      >
        {/* Loading placeholder that maintains aspect ratio */}
        {isLoading && (
          <div className="absolute inset-0 bg-transparent flex items-center justify-center">
            <div className="text-gray-400 text-sm">Loading...</div>
          </div>
        )}
        
        <img
          src={currentSrc}
          alt={alt}
          className="w-full h-full object-cover transition-opacity duration-300"
          loading={loading}
          style={{
            opacity: isLoading ? 0 : 1
          }}
          onError={() => {
            // Additional error handling at img level
            if (!hasError) {
              console.warn('🔄 [CachedImage] Img element error, using fallback');
              setHasError(true);
              setCurrentSrc(fallbackSrc);
            }
          }}
        />
      </div>
    );
  }

  // Default behavior without aspect ratio preservation
  return (
    <img
      src={currentSrc}
      alt={alt}
      className={`${className} transition-opacity duration-300`}
      loading={loading}
      style={{
        opacity: isLoading ? 0.7 : 1
      }}
      onError={() => {
        // Additional error handling at img level
        if (!hasError) {
          console.warn('🔄 [CachedImage] Img element error, using fallback');
          setHasError(true);
          setCurrentSrc(fallbackSrc);
        }
      }}
    />
  );
};

// Cache statistics for debugging
export const getCacheStats = () => ({
  size: imageCache.size,
  maxSize: MAX_CACHE_SIZE,
  urls: Array.from(imageCache.keys()).map(url => url.split('/').pop())
});

// Clear cache manually (for development/debugging)
export const clearImageCache = () => {
  imageCache.clear();
};

// Preload important images
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already cached
    if (imageCache.has(src)) {
      resolve();
      return;
    }

    const img = new Image();
    img.onload = () => {
      imageCache.set(src, {
        imageElement: img,
        timestamp: Date.now(),
        url: src
      });
      resolve();
    };
    img.onerror = reject;
    img.src = src;
  });
};

// Auto-cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    imageCache.clear();
  });
} 